import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronLeft, Phone } from 'lucide-react';
import { useRestaurant } from '../../context/RestaurantContext';
import { useSiteWorkspace } from '../../context/SiteWorkspaceContext';
import ArabAliLogo from '../../components/ArabAliLogo';
import SmartImagePreview from '../../components/shared/SmartImagePreview';

const ItemDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { phoneNumber, restaurant, settings, callHref, formatMoney } = useRestaurant();
  const { getItemById, getCategoryById, items } = useSiteWorkspace();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const rawItem = id ? getItemById(id) : undefined;
  const item = rawItem?.isVisible ? rawItem : undefined;
  const category = item ? getCategoryById(item.categoryId) : undefined;

  const heroImageSrc = useMemo(() => {
    if (!item) return restaurant.coverImage;
    if (item.imageUrl?.trim()) return item.imageUrl.trim();
    if (category?.coverImage?.trim()) return category.coverImage.trim();
    return restaurant.coverImage;
  }, [item, category, restaurant.coverImage]);

  const heroImageAlt = useMemo(() => {
    if (!item) return restaurant.name;
    const alt = item.imageAlt?.trim();
    if (alt) return alt;
    return item.name;
  }, [item, restaurant.name]);

  const relatedItems = useMemo(() => {
    if (!item) return [];
    return items
      .filter((entry) => entry.categoryId === item.categoryId && entry.id !== item.id && entry.isVisible)
      .slice(0, 4);
  }, [item, items]);

  if (!item) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="text-center">
          <div className="mb-2 text-4xl">😕</div>
          <p className="text-white/60">Menu item not found</p>
          <button type="button" onClick={() => navigate(-1)} className="mt-2 inline-flex items-center gap-2 text-amber-300">
            <ArrowLeft size={20} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pb-12 pt-4">
      <div className="mx-auto max-w-6xl">
        <div className="mb-4 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 transition-colors hover:bg-white/10"
          >
            <ChevronLeft className="h-6 w-6 text-white" />
          </button>
          <Link to="/menu" className="text-sm uppercase tracking-[0.25em] text-amber-300 hover:text-amber-200">
            Back to menu
          </Link>
        </div>

        <div className="grid gap-5 lg:grid-cols-[0.88fr_1.12fr]">
          <motion.div
            initial={{ opacity: 0, x: -18 }}
            animate={{ opacity: 1, x: 0 }}
            className="arab-panel overflow-hidden p-3"
          >
            <div className="relative overflow-hidden rounded-[28px] border border-amber-300/10">
              <SmartImagePreview
                src={heroImageSrc}
                alt={heroImageAlt}
                fit="cover"
                className="min-h-[260px] rounded-none border-0 sm:min-h-[320px] lg:min-h-[520px]"
                imageClassName="min-h-[260px] sm:min-h-[320px] lg:min-h-[520px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#100605] via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 z-[2] p-5">
                <ArabAliLogo compact logoImage={settings.logoImage} name={restaurant.name} />
                <div className="mt-4 text-[11px] uppercase tracking-[0.35em] text-amber-100/55">{category?.title}</div>
                <div className="mt-2 arab-gold-text text-3xl font-semibold">{item.name}</div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} className="arab-panel p-6 sm:p-8">
            <div className="text-[11px] uppercase tracking-[0.35em] text-amber-100/50">{category?.title}</div>
            <h1 className="mt-3 font-heading text-3xl font-semibold text-white sm:text-5xl">{item.name}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <div className="arab-price-pill text-lg">{formatMoney(item.price)}</div>
              {!item.isAvailable && (
                <div className="rounded-full border border-red-300/20 bg-red-500/10 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-red-200">
                  Unavailable
                </div>
              )}
            </div>

            {item.description && (
              <div className="mt-5 rounded-3xl border border-white/10 bg-white/5 p-5">
                <div className="text-[11px] uppercase tracking-[0.35em] text-white/45">Details</div>
                <p className="mt-3 text-base leading-7 text-white/75">{item.description}</p>
              </div>
            )}

            <div className="mt-5 rounded-3xl border border-white/10 bg-white/5 p-5">
              <div className="flex items-center gap-2 text-amber-300">
                <Phone className="h-4 w-4" />
                <span className="text-[11px] uppercase tracking-[0.35em] text-white/45">Call the Restaurant</span>
              </div>
              <div className="mt-3 text-lg text-white">{phoneNumber}</div>
              <div className="mt-2 text-sm leading-6 text-white/55">
                Guests already inside the restaurant can use the QR code to browse, then call to place the order.
              </div>
            </div>

            {callHref ? (
              <a href={callHref} className="arab-button-primary mt-5 inline-flex w-full items-center justify-center gap-3">
                <Phone className="h-5 w-5" />
                {settings.callToActionLabel}
              </a>
            ) : (
              <div className="mt-5 rounded-3xl border border-white/10 bg-white/5 px-5 py-4 text-center text-white/70">
                Call ordering will be available once the restaurant phone number is added.
              </div>
            )}

            {relatedItems.length > 0 && (
              <div className="mt-8">
                <div className="mb-4 text-[11px] uppercase tracking-[0.35em] text-amber-100/50">
                  More from {category?.title}
                </div>
                <div className="space-y-3">
                  {relatedItems.map((relatedItem) => (
                    <Link key={relatedItem.id} to={`/item/${relatedItem.id}`} className="arab-list-row group block">
                      <div className="flex min-w-0 items-center gap-4">
                        <SmartImagePreview
                          src={relatedItem.imageUrl || category?.coverImage || restaurant.coverImage}
                          alt={relatedItem.imageAlt || relatedItem.name}
                          className="h-16 w-16 shrink-0"
                        />
                        <div className="min-w-0">
                          <div className="text-lg font-semibold text-white transition-colors group-hover:text-amber-200">
                          {relatedItem.name}
                          </div>
                          {relatedItem.description && (
                            <div className="mt-1 line-clamp-2 text-sm text-white/50">{relatedItem.description}</div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="arab-price-pill">{formatMoney(relatedItem.price)}</div>
                        <ArrowLeft className="h-4 w-4 rotate-180 text-amber-300/60 transition-transform group-hover:translate-x-1" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ItemDetailPage;
